/**
 * TradeStack - API Client
 *
 * Axios instance with interceptors for authentication
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// AUTH API
// =============================================================================

export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  validate: () => api.get('/auth/validate'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  updateProfile: (data) => api.put('/auth/profile', data)
};

// =============================================================================
// CONFIG API
// =============================================================================

export const configApi = {
  getPublic: () => api.get('/config/public')
};

// =============================================================================
// CUSTOMERS API
// =============================================================================

export const customersApi = {
  getAll: (params) => api.get('/customers', { params }),
  getOne: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`)
};

// =============================================================================
// JOBS API
// =============================================================================

export const jobsApi = {
  getAll: (params) => api.get('/jobs', { params }),
  getOne: (id) => api.get(`/jobs/${id}`),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  updateStatus: (id, status) => api.patch(`/jobs/${id}/status`, { status }),
  addNote: (id, data) => api.post(`/jobs/${id}/notes`, data),
  delete: (id) => api.delete(`/jobs/${id}`)
};

// =============================================================================
// ESTIMATES API
// =============================================================================

export const estimatesApi = {
  getAll: (params) => api.get('/estimates', { params }),
  getOne: (id) => api.get(`/estimates/${id}`),
  create: (data) => api.post('/estimates', data),
  updateStatus: (id, status) => api.patch(`/estimates/${id}/status`, { status }),
  delete: (id) => api.delete(`/estimates/${id}`)
};

// =============================================================================
// INVOICES API
// =============================================================================

export const invoicesApi = {
  getAll: (params) => api.get('/invoices', { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  recordPayment: (id, data) => api.post(`/invoices/${id}/payments`, data),
  updateStatus: (id, status) => api.patch(`/invoices/${id}/status`, { status }),
  delete: (id) => api.delete(`/invoices/${id}`)
};

// =============================================================================
// EXPENSES API
// =============================================================================

export const expensesApi = {
  getAll: (params) => api.get('/expenses', { params }),
  getOne: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`)
};

// =============================================================================
// RECEIPTS API
// =============================================================================

export const receiptsApi = {
  getAll: (params) => api.get('/receipts', { params }),
  scan: (formData) => api.post('/receipts/scan', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  confirm: (id, data) => api.post(`/receipts/${id}/confirm`, data),
  delete: (id) => api.delete(`/receipts/${id}`)
};

// =============================================================================
// MILEAGE API
// =============================================================================

export const mileageApi = {
  getAll: (params) => api.get('/mileage', { params }),
  getOne: (id) => api.get(`/mileage/${id}`),
  create: (data) => api.post('/mileage', data),
  update: (id, data) => api.put(`/mileage/${id}`, data),
  delete: (id) => api.delete(`/mileage/${id}`),
  compareFuel: (year) => api.get('/mileage/compare/fuel', { params: { year } })
};

// =============================================================================
// REPORTS API
// =============================================================================

export const reportsApi = {
  getDashboard: () => api.get('/reports/dashboard'),
  getProfitLoss: (params) => api.get('/reports/profit-loss', { params }),
  getTaxEstimate: (year) => api.get('/reports/tax-estimate', { params: { year } }),
  getExpensesByCategory: (params) => api.get('/reports/expenses-by-category', { params }),
  getSubcontractor1099: (year) => api.get('/reports/subcontractor-1099', { params: { year } })
};

// =============================================================================
// UNIFIED API INTERFACE (for cleaner usage in components)
// =============================================================================

const apiClient = {
  // Auth
  register: authApi.register,
  login: authApi.login,
  validate: authApi.validate,
  requestPasswordReset: (email) => authApi.forgotPassword(email),
  resetPassword: authApi.resetPassword,
  updateProfile: authApi.updateProfile,

  // Config
  getPublicConfig: configApi.getPublic,

  // Customers
  getCustomers: customersApi.getAll,
  getCustomer: customersApi.getOne,
  createCustomer: customersApi.create,
  updateCustomer: customersApi.update,
  deleteCustomer: customersApi.delete,

  // Jobs
  getJobs: jobsApi.getAll,
  getJob: jobsApi.getOne,
  createJob: jobsApi.create,
  updateJob: jobsApi.update,
  updateJobStatus: jobsApi.updateStatus,
  addJobNote: jobsApi.addNote,
  deleteJob: jobsApi.delete,

  // Estimates
  getEstimates: estimatesApi.getAll,
  getEstimate: estimatesApi.getOne,
  createEstimate: estimatesApi.create,
  updateEstimateStatus: estimatesApi.updateStatus,
  deleteEstimate: estimatesApi.delete,

  // Invoices
  getInvoices: invoicesApi.getAll,
  getInvoice: invoicesApi.getOne,
  createInvoice: invoicesApi.create,
  recordPayment: invoicesApi.recordPayment,
  updateInvoiceStatus: invoicesApi.updateStatus,
  deleteInvoice: invoicesApi.delete,

  // Expenses
  getExpenses: expensesApi.getAll,
  getExpense: expensesApi.getOne,
  createExpense: expensesApi.create,
  updateExpense: expensesApi.update,
  deleteExpense: expensesApi.delete,

  // Receipts
  getReceipts: receiptsApi.getAll,
  uploadReceipt: receiptsApi.scan,
  updateReceipt: (id, data) => api.put(`/receipts/${id}`, data),
  createExpenseFromReceipt: (id) => api.post(`/receipts/${id}/create-expense`),
  deleteReceipt: receiptsApi.delete,

  // Mileage
  getMileageLogs: mileageApi.getAll,
  getMileageLog: mileageApi.getOne,
  createMileageLog: mileageApi.create,
  updateMileageLog: mileageApi.update,
  deleteMileageLog: mileageApi.delete,
  compareMileageToFuel: mileageApi.compareFuel,

  // Reports
  getDashboard: reportsApi.getDashboard,
  getProfitLossReport: reportsApi.getProfitLoss,
  getTaxEstimate: reportsApi.getTaxEstimate,
  getExpensesByCategory: reportsApi.getExpensesByCategory,
  get1099Report: reportsApi.getSubcontractor1099,

  // Settings
  getUserSettings: () => api.get('/auth/settings'),
  updateUserSettings: (data) => api.put('/auth/settings', data),
  updateBranding: (data) => api.put('/auth/branding', data)
};

export default apiClient;
